import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
	checkSupabaseConnection,
	type SupabaseConnectionResult,
} from "@shared/integrations/supabase/connectionCheck";

type ConnectionStatus = "checking" | "ok" | "error";
const CONNECTION_ERROR_FALLBACK =
	"Unable to reach Supabase. DSLM Workspace cannot run without it.";

const Auth = () => {
	const { user, signIn, signUp, resetPassword } = useAuth();
	const { toast } = useToast();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [showResetLink, setShowResetLink] = useState(false);
	const [isResettingPassword, setIsResettingPassword] = useState(false);
	const [connectionStatus, setConnectionStatus] =
		useState<ConnectionStatus>("checking");
	const [connectionError, setConnectionError] = useState<string | null>(null);
	const isMountedRef = useRef(true);
	const connectionInProgress = connectionStatus === "checking";
	const connectionReady = connectionStatus === "ok";
	const disableAuthActions = loading || !connectionReady;

	const applyConnectionResult = useCallback(
		(result: SupabaseConnectionResult) => {
			if (!isMountedRef.current) return;

			if (result.ok) {
				setConnectionStatus("ok");
				setConnectionError(null);
				return;
			}

			setConnectionStatus("error");
			setConnectionError(result.error.message || CONNECTION_ERROR_FALLBACK);
		},
		[],
	);

	const verifyConnection = useCallback(async () => {
		setConnectionStatus("checking");
		const result = await checkSupabaseConnection();
		applyConnectionResult(result);
	}, [applyConnectionResult]);

	useEffect(() => {
		void verifyConnection();
		return () => {
			isMountedRef.current = false;
		};
	}, [verifyConnection]);

	// Redirect if already authenticated
	if (user) {
		return <Navigate to="/workspace" replace />;
	}

	if (connectionStatus === "error") {
		return (
			<div className="min-h-screen flex items-center justify-center bg-background px-4">
				<Card className="w-full max-w-md border-destructive/50">
					<CardHeader>
						<CardTitle>500 · Supabase connection failed</CardTitle>
						<CardDescription>
							DSLM Workspace cannot function because it cannot reach Supabase.
							Verify the Supabase stack is running and environment variables
							point to it.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<p className="text-sm font-mono text-destructive break-words">
							{connectionError ?? CONNECTION_ERROR_FALLBACK}
						</p>
						<p className="text-sm text-muted-foreground">
							Common fixes: run <code>supabase start</code> locally or update{" "}
							<code className="mx-1">SUPABASE_URL</code> and{" "}
							<code className="mx-1">SUPABASE_ANON_KEY</code> to the correct
							project.
						</p>
						<Button
							variant="outline"
							onClick={verifyConnection}
							disabled={connectionInProgress}
						>
							Retry connection
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	const handleSignIn = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);
		setShowResetLink(false);

		const { error } = await signIn(email, password);

		if (error) {
			toast({
				title: "Sign in failed",
				description: error.message,
				variant: "destructive",
			});
			setShowResetLink(true);
		}

		setLoading(false);
	};

	const handleResetPassword = async () => {
		if (!email) {
			toast({
				title: "Email required",
				description: "Please enter your email address first.",
				variant: "destructive",
			});
			return;
		}

		setIsResettingPassword(true);
		const { error } = await resetPassword(email);

		if (error) {
			toast({
				title: "Password reset failed",
				description: error.message,
				variant: "destructive",
			});
		} else {
			toast({
				title: "Check your email",
				description: "We've sent you a password reset link.",
			});
			setShowResetLink(false);
		}

		setIsResettingPassword(false);
	};

	const handleSignUp = async (e: React.FormEvent) => {
		e.preventDefault();
		setLoading(true);

		const { error } = await signUp(email, password);

		if (error) {
			toast({
				title: "Sign up failed",
				description: error.message,
				variant: "destructive",
			});
		} else {
			toast({
				title: "Check your email",
				description: "We've sent you a confirmation link.",
			});
		}

		setLoading(false);
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-background px-4">
			<Card className="w-full max-w-md">
				<CardHeader>
					<CardTitle>AI Chat Workspace</CardTitle>
					<CardDescription>
						Sign in to your account or create a new one
					</CardDescription>
				</CardHeader>
				<CardContent>
					<Tabs defaultValue="signin" className="w-full">
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="signin">Sign In</TabsTrigger>
							<TabsTrigger value="signup">Sign Up</TabsTrigger>
						</TabsList>
						<TabsContent value="signin">
							<form onSubmit={handleSignIn} className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="email">Email</Label>
									<Input
										id="email"
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="password">Password</Label>
									<Input
										id="password"
										type="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
									/>
								</div>
								<Button
									type="submit"
									className="w-full"
									disabled={disableAuthActions}
								>
									{connectionInProgress
										? "Connecting to Supabase..."
										: loading
											? "Signing in..."
											: "Sign In"}
								</Button>
								{showResetLink && (
									<div className="text-center mt-4">
										<Button
											type="button"
											variant="link"
											onClick={handleResetPassword}
											disabled={isResettingPassword || !connectionReady}
											className="text-sm"
										>
											{isResettingPassword
												? "Sending reset link..."
												: "Forgot password? Reset it here"}
										</Button>
									</div>
								)}
							</form>
						</TabsContent>
						<TabsContent value="signup">
							<form onSubmit={handleSignUp} className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="signup-email">Email</Label>
									<Input
										id="signup-email"
										type="email"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="signup-password">Password</Label>
									<Input
										id="signup-password"
										type="password"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										required
									/>
								</div>
								<Button
									type="submit"
									className="w-full"
									disabled={disableAuthActions}
								>
									{connectionInProgress
										? "Connecting to Supabase..."
										: loading
											? "Creating account..."
											: "Sign Up"}
								</Button>
							</form>
						</TabsContent>
					</Tabs>
				</CardContent>
			</Card>
		</div>
	);
};

export default Auth;
